/**
 * LeaderboardBlock — Platform-standard animated leaderboard
 *
 * Data sources:
 * - source "COMPETITION": useGetLeaderboardStandings + useLeaderboardWebSocket (real-time)
 * - source "HUB_POINTS": useGetDropMemberLeaderboard (60s polling)
 * - source "LEDGER": useGetLedgerStandings (60s polling) — a LedgerBoard board over the
 *   point ledger. Rows arrive server-redacted (displayName resolved, userId only on the
 *   caller's own row) and carry an optional current-period/all-time switch when the
 *   board's showAllTime is set.
 *
 * All normalize into LeaderboardRowData for a single animated renderer featuring:
 * - CountUp score transitions (react-countup)
 * - Rank swap slide animations (CSS keyframes)
 * - Gold trophy treatment for top 3
 * - Live pulse indicator (green dot)
 * - Platform icons (PS/Xbox/PC) when showPlatformIcons enabled
 * - Current user highlight using hub theme accent
 * - Scroll-reveal entrance animation via IntersectionObserver
 *
 * Colors come from hub CSS variables — NOT hardcoded. Animation quality is the platform standard.
 *
 * Do NOT use /tournament/ routes — those are bracket-only.
 * See CLAUDE.md "Competition System Architecture" for route decision rules.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import CountUp from "react-countup";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { FaTrophy, FaPlaystation, FaXbox, FaDesktop } from "react-icons/fa";
import { SurfaceLeaderboardBlockConfigResponse } from "@/hooks/hub/useGetHub";
import { useGetLeaderboardStandings, useGetMyLeaderboardRank } from "@/competitions/leaderboards/hooks/useGetLeaderboard";
import { useGetDropMemberLeaderboard } from "@/hooks/drop/useGetDropMemberLeaderboard";
import { useLeaderboardWebSocket } from "@/competitions/leaderboards/hooks/useLeaderboardWebSocket";
import { LedgerBoard } from "./LedgerBoard";
import "./LeaderboardBlock.scss";

/** Normalized row shape — both sources convert into this */
interface LeaderboardRowData {
    userId: string;
    rank: number;
    previousRank?: number;
    displayName: string;
    avatarUrl?: string;
    score: number;
    previousScore?: number;
    platform?: string;
    isCurrentUser?: boolean;
}

/*
 * Avatar cell with a broken-image fallback. avatarUrl can point at a CDN object
 * that 404s (an Asset row whose primary .webp was never written), and a bare
 * <img> would render the browser's broken-image glyph. On load error we fall
 * back to the initials placeholder. Scoped per row so one broken avatar doesn't
 * affect the others; the URL is the key so a fixed asset re-attempts to load.
 */
const LeaderboardAvatar: React.FC<{ avatarUrl?: string; initial: string }> = ({ avatarUrl, initial }) => {
    const [failed, setFailed] = useState(false);
    return avatarUrl && !failed
        ? <img src={avatarUrl} alt="" className="leaderboard-block__avatar-img" onError={() => setFailed(true)} />
        : <div className="leaderboard-block__avatar-placeholder">{initial}</div>;
};

interface LeaderboardBlockProps {
    config?: SurfaceLeaderboardBlockConfigResponse;
    sectionTitle?: string;
    /** Hub slug — required for HUB_POINTS source */
    surfaceSlug?: string;
}

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
    playstation: <FaPlaystation />,
    xbox: <FaXbox />,
    pc: <FaDesktop />,
};


export const LeaderboardBlock = ({ config, sectionTitle, surfaceSlug }: LeaderboardBlockProps) => {
    const currentUserId = useSelector((store: IStore) => store.authStore.fullToken?.id);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [animatingIds, setAnimatingIds] = useState<Set<string>>(new Set());
    const prevRowsRef = useRef<Map<string, LeaderboardRowData>>(new Map());

    const source = config?.source || "COMPETITION";
    const maxRows = config?.maxRows || 10;
    const layout = config?.layout || "full";
    const showPlatformIcons = config?.showPlatformIcons !== false;
    const showCurrentUser = config?.showCurrentUser !== false;
    const showMyPosition = config?.showMyPosition === true;

    // ── Competition source ──────────────────────────────────
    const competitionEnabled = source === "COMPETITION" && !!config?.competitionId;
    const { data: standingsData, isLoading: standingsLoading, error: standingsError } = useGetLeaderboardStandings(
        competitionEnabled ? config?.competitionId : undefined,
        maxRows,
        0,
    );

    /*
     * Real-time standings — join the leaderboard room and patch the
     * ["leaderboard-standings", competitionId] cache on every broadcast. Without
     * this the block was static despite the docstring claiming live updates.
     * competitionId is the same id the standings hook and backend room are keyed on.
     */
    useLeaderboardWebSocket(
        competitionEnabled ? config?.competitionId : undefined,
        competitionEnabled ? config?.competitionId : undefined,
        competitionEnabled,
    );

    /*
     * My-rank — the standings list is capped at maxRows, so a user ranked below
     * the cutoff is absent from `rows` and the My Position card falsely showed
     * "Not ranked yet". This dedicated endpoint returns the caller's true rank.
     */
    const { data: myRankData } = useGetMyLeaderboardRank(competitionEnabled && showMyPosition ? config?.competitionId : undefined);

    // ── Hub Points source ───────────────────────────────────
    const hubPointsEnabled = source === "HUB_POINTS" && !!surfaceSlug;
    const { data: hubPointsData, isLoading: hubPointsLoading, error: hubPointsError } = useGetDropMemberLeaderboard(
        hubPointsEnabled ? surfaceSlug : undefined,
        0,
        maxRows,
    );

    // ── Normalize rows ──────────────────────────────────────
    const rows: LeaderboardRowData[] = useMemo(() => {
        if (source === "COMPETITION" && standingsData?.standings) {
            return standingsData.standings.map((s) => ({
                userId: s.userId,
                rank: s.rank,
                previousRank: s.previousRank,
                displayName: s.displayName,
                avatarUrl: s.avatarUrl,
                score: s.value,
                isCurrentUser: showCurrentUser && s.userId === currentUserId,
            }));
        }

        if (source === "HUB_POINTS" && hubPointsData?.entries) {
            return hubPointsData.entries.map((e) => ({
                userId: e.userId,
                rank: e.rank,
                displayName: e.displayName,
                avatarUrl: e.avatarUrl,
                score: e.balance,
                isCurrentUser: showCurrentUser && e.userId === currentUserId,
            }));
        }

        if (source === "DEMO" && config?.demoEntries?.length) {
            return config.demoEntries.map((d) => ({
                userId: `demo-${d.rank}`,
                rank: d.rank,
                previousRank: d.change ? d.rank + d.change : undefined,
                displayName: d.name,
                avatarUrl: d.avatarUrl,
                score: d.score,
                isCurrentUser: false,
            }));
        }

        return [];
    }, [source, standingsData, hubPointsData, config?.demoEntries, currentUserId, showCurrentUser]);

    // ── Track previous scores/ranks for animation ───────────
    const animatedRows: LeaderboardRowData[] = useMemo(() => {
        const prevMap = prevRowsRef.current;
        return rows.map((row) => {
            const prev = prevMap.get(row.userId);
            return {
                ...row,
                previousScore: prev?.score,
                previousRank: row.previousRank ?? prev?.rank,
            };
        });
    }, [rows]);

    // Update previous rows ref after render — detect rank changes for animation
    useEffect(() => {
        const newMap = new Map<string, LeaderboardRowData>();
        rows.forEach((r) => newMap.set(r.userId, r));

        const prevMap = prevRowsRef.current;
        const changedIds = new Set<string>();
        rows.forEach((r) => {
            const prev = prevMap.get(r.userId);
            if (prev && prev.rank !== r.rank) {
                changedIds.add(r.userId);
            }
        });

        // Always update ref before returning cleanup, so next render compares against latest
        prevRowsRef.current = newMap;

        if (changedIds.size > 0) {
            setAnimatingIds(changedIds);
            const timer = setTimeout(() => setAnimatingIds(new Set()), 3000);
            return () => clearTimeout(timer);
        }
    }, [rows]);

    // ── Scroll-reveal entrance ──────────────────────────────
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 },
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // ── Rank change helper ──────────────────────────────────
    const getRankChange = useCallback((current: number, previous?: number) => {
        if (previous == null || previous === current) return null;
        const diff = previous - current;
        return {
            direction: diff > 0 ? "up" as const : "down" as const,
            amount: Math.abs(diff),
        };
    }, []);

    if (!config) return null;

    /*
     * LEDGER renders through its own anchor-spec board, not the generic skin —
     * and a LEDGER block naming no board renders NOTHING.
     *
     * Falling through handed it to the generic skin, which reads only
     * competition and hub-points data and so returned zero rows every time:
     * the page printed "No rankings yet. Be the first to compete!" beside a
     * board that was showing standings. An unset board is not an empty board.
     */
    if (source === "LEDGER") {
        if (!config.ledgerBoardId) return null;
        return <LedgerBoard config={config} sectionTitle={sectionTitle} />;
    }

    const isDemo = source === "DEMO";
    const isLoading = isDemo ? false : competitionEnabled ? standingsLoading : hubPointsLoading;
    const error = isDemo ? null : competitionEnabled ? standingsError : hubPointsError;

    // ── Loading skeleton ────────────────────────────────────
    if (isLoading) {
        return (
            <div className={`leaderboard-block leaderboard-block--${layout}`}>
                {sectionTitle && <div className="leaderboard-block__header"><h3 className="leaderboard-block__title">{sectionTitle}</h3></div>}
                <div className="leaderboard-block__skeleton">
                    {Array.from({ length: Math.min(maxRows, 5) }).map((_, i) =>
                        <div key={i} className="leaderboard-block__skeleton-row" style={{ animationDelay: `${i * 0.1}s` }} />)}
                </div>
            </div>
        );
    }

    // ── Error state ─────────────────────────────────────────
    if (error) {
        return (
            <div className={`leaderboard-block leaderboard-block--${layout}`}>
                {sectionTitle && <div className="leaderboard-block__header"><h3 className="leaderboard-block__title">{sectionTitle}</h3></div>}
                <div className="leaderboard-block__error">
                    Unable to load leaderboard. Please try again later.
                </div>
            </div>
        );
    }

    // ── Empty state ─────────────────────────────────────────
    if (animatedRows.length === 0) {
        return (
            <div className={`leaderboard-block leaderboard-block--${layout}`}>
                {sectionTitle && <div className="leaderboard-block__header"><h3 className="leaderboard-block__title">{sectionTitle}</h3></div>}
                <div className="leaderboard-block__empty">
                    No rankings yet. Be the first to compete!
                </div>
            </div>
        );
    }

    // ── Main render ─────────────────────────────────────────
    return (
        <div
            ref={containerRef}
            className={`leaderboard-block leaderboard-block--${layout} ${isVisible ? "leaderboard-block--entering" : ""}`}
        >
            <div className="leaderboard-block__header">
                {sectionTitle && <h3 className="leaderboard-block__title">{sectionTitle}</h3>}
                <div className="leaderboard-block__live-indicator">
                    <span className="leaderboard-block__live-dot" />
                    LIVE
                </div>
            </div>

            {/* My Position card — compact user rank block above the table */}
            {showMyPosition && showCurrentUser && (() => {
                if (!currentUserId) {
                    return (
                        <div className="leaderboard-block__my-pos-card leaderboard-block__my-pos-card--guest">
                            <span className="leaderboard-block__my-pos-label">Your Position</span>
                            <span className="leaderboard-block__my-pos-pending">Log in to see your ranking</span>
                        </div>
                    );
                }
                const userRow = animatedRows.find(r => r.isCurrentUser);
                return (
                    <div className="leaderboard-block__my-pos-card">
                        <span className="leaderboard-block__my-pos-label">Your Position</span>
                        {userRow
                            ? <>
                                <span className="leaderboard-block__my-pos-rank">#{userRow.rank}</span>
                                <span className="leaderboard-block__my-pos-score">{userRow.score.toLocaleString()} pts</span>
                            </>
                            : myRankData?.rank != null
                                ? <>
                                    <span className="leaderboard-block__my-pos-rank">#{myRankData.rank}</span>
                                    <span className="leaderboard-block__my-pos-score">{myRankData.value.toLocaleString()} pts</span>
                                </>
                                : <span className="leaderboard-block__my-pos-pending">Not ranked yet</span>
                        }
                    </div>
                );
            })()}

            <div className="leaderboard-block__table">
                {/* Column Headers */}
                <div className="leaderboard-block__column-headers">
                    <span className="leaderboard-block__column-header leaderboard-block__column-header--center">Rank</span>
                    {layout === "full" && <span className="leaderboard-block__column-header" />}
                    <span className="leaderboard-block__column-header">Player</span>
                    <span className="leaderboard-block__column-header leaderboard-block__column-header--right">Score</span>
                    {layout === "full" && <span className="leaderboard-block__column-header leaderboard-block__column-header--right" />}
                </div>

                {animatedRows.map((row) => {
                    const rankChange = getRankChange(row.rank, row.previousRank);
                    const isAnimating = animatingIds.has(row.userId);
                    const isTopThree = row.rank <= 3;
                    const initial = row.displayName?.charAt(0)?.toUpperCase() || "?";

                    return (
                        <div
                            key={row.userId}
                            className={[
                                "leaderboard-block__row",
                                isTopThree ? "leaderboard-block__row--top3" : "",
                                row.isCurrentUser ? "leaderboard-block__row--current" : "",
                                isAnimating ? "leaderboard-block__row--animating" : "",
                                isAnimating && rankChange?.direction === "up" ? "leaderboard-block__row--moving-up" : "",
                                isAnimating && rankChange?.direction === "down" ? "leaderboard-block__row--moving-down" : "",
                            ].filter(Boolean).join(" ")}
                        >
                            {/* Rank */}
                            <div className="leaderboard-block__rank">
                                <span className="leaderboard-block__rank-display">
                                    {isTopThree && <FaTrophy className="leaderboard-block__trophy" />}
                                    <span className="leaderboard-block__rank-number">{row.rank}</span>
                                </span>
                            </div>

                            {/* Avatar (full layout only) */}
                            {layout === "full" &&
                                <div className="leaderboard-block__avatar">
                                    <LeaderboardAvatar avatarUrl={row.avatarUrl} initial={initial} />
                                </div>
                            }

                            {/* User */}
                            <div className="leaderboard-block__user">
                                <span className="leaderboard-block__username">
                                    {row.displayName}
                                    {showPlatformIcons && row.platform && PLATFORM_ICONS[row.platform] &&
                                        <span className="leaderboard-block__platform-icon" title={row.platform}>
                                            {PLATFORM_ICONS[row.platform]}
                                        </span>
                                    }
                                </span>
                            </div>

                            {/* Score with CountUp */}
                            <div className="leaderboard-block__score">
                                <CountUp
                                    start={row.previousScore ?? row.score}
                                    end={row.score}
                                    duration={2.5}
                                    separator=","
                                    useEasing={true}
                                    preserveValue={true}
                                />
                            </div>

                            {/* Rank Change (full layout only) */}
                            {layout === "full" &&
                                <div className="leaderboard-block__change">
                                    {rankChange
                                        ? <span className={`leaderboard-block__change-indicator leaderboard-block__change-indicator--${rankChange.direction}`}>
                                            {rankChange.direction === "up" ? "▲" : "▼"} {rankChange.amount}
                                        </span>
                                        : <span className="leaderboard-block__change-indicator leaderboard-block__change-indicator--same">
                                            —
                                        </span>
                                    }
                                </div>
                            }
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
